import React from 'react';
import styled from '@emotion/styled';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { Textfit } from 'react-textfit';
import { useI18n } from '~Hooks';
import { Box } from '~Styles';
import { useStorage } from '~Components/Storage';

export const ButtonWrapper = styled.div<{
  color?: string;
  touching?: boolean;
  disabled: boolean;
  width: string;
  hasAvailableInventory?: boolean | undefined;
}>`
  text-align: center;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  width: ${({ width }) => width};
  height: 88px;
  margin: 4px;
  border-radius: 4px;
  box-sizing: border-box;
  padding: 10px 4px;
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
  background: ${({ color }) => color || '#fff'};
  box-shadow: 0px 0px 16px 4px rgba(0, 0, 0, 0.08), 0px 1px 0px rgba(0, 0, 0, 0.16),
    0px 1px 3px rgba(0, 0, 0, 0.16);
  position: relative;
  overflow: hidden;
  word-break: break-word;

  ${({ touching, hasAvailableInventory }) =>
    touching &&
    `
    z-index: 10;
    box-shadow: ${
      hasAvailableInventory === false
        ? `0 0 0 4px rgb(255 0 0 / 40%)`
        : `0 0 0 4px rgb(255 255 255 / 30%)`
    };
  `}

  .SVG-icon {
    position: absolute;
    left: 4px;
    bottom: 4px;
  }
`;

export const QuantityWrapper = styled.div`
  position: absolute;
  bottom: 4px;
  right: 4px;
  padding: 0 8px 2px;
  background-color: var(--colorBlueGray1200);
  border-radius: 12px;
  height: 24px;
  min-width: 32px;
  box-sizing: border-box;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
`;

const ImageRect = styled.div`
  width: 72px;
  height: 72px;
  margin-left: 4px;
  border-radius: 4px;
  overflow: hidden;
  flex: 0 0 auto;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const TitleWrapper = styled.div<{ hasImage: boolean }>`
  height: 100%;
  font-style: normal;
  line-height: var(--lineHeightLarge);
  letter-spacing: var(--letterSpacing);
  font-weight: 600;
  flex: 1 1 auto;
  text-align: ${({ hasImage }) => (hasImage ? 'left' : 'center')};

  & > div {
    height: 100%;
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    align-items: ${({ hasImage }) => (hasImage ? 'flex-start' : 'center')};
  }
`;

const NrInCart = styled.div<{ showImage: boolean; color?: string }>`
  position: absolute;
  bottom: 5px;
  left: 4px;
  padding: 4px;
  height: ${({ showImage }) => (showImage ? '16px' : '24px')};
  box-sizing: border-box;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  background-color: ${({ showImage, color }) => (showImage ? color : 'transparent')};
  border-radius: ${({ showImage }) => (showImage ? '4px' : 'auto')};
`;

const Trophy = styled.div`
  position: absolute;
  top: -4px;
  left: 2px;
  font-size: 25px;

  .SVG-icon {
    position: relative;
    left: 0;
    bottom: 0;
  }
`;

export interface Props {
  touching: boolean;
  color: string;
  itemId: string;
  disableButton: boolean;
  handleTouchStart: () => void;
  handleTouchEnd: () => void;
  buttonWidth: number;
  hasAvailableInventory?: boolean;
  title: string;
  subTitle?: string;
  volumeSuffix: string | null;
  nrInCart: number;
  inventoryStatus: {
    outOfStock: boolean;
    count: number | null;
  };
  isPartOfCompetition?: boolean;
  imageUrl?: string;
}

const Button = ({
  touching,
  color,
  itemId,
  disableButton,
  handleTouchStart,
  handleTouchEnd,
  buttonWidth,
  hasAvailableInventory,
  title,
  subTitle,
  volumeSuffix,
  nrInCart,
  inventoryStatus,
  isPartOfCompetition = false,
  imageUrl,
}: Props) => {
  const { i18n } = useI18n();
  const [storage] = useStorage();

  return (
    <ButtonWrapper
      color={color}
      key={itemId}
      touching={touching}
      disabled={disableButton}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      width={`calc(${buttonWidth}% - 8px)`}
      hasAvailableInventory={hasAvailableInventory}>
      <Box
        display="flex"
        gap="xs"
        justifyContent={storage.showImagesOnButtons && imageUrl ? 'flex-start' : 'center'}
        fluid>
        {storage.showImagesOnButtons && imageUrl && (
          <ImageRect>
            <img src={`${imageUrl}/300x300`} alt={title} />
          </ImageRect>
        )}
        <TitleWrapper hasImage={Boolean(storage.showImagesOnButtons && imageUrl)}>
          <Textfit min={12} max={16}>
            {title}
            {volumeSuffix && <Paragraph level={5}>{volumeSuffix}</Paragraph>}
            {subTitle && (
              <Paragraph level={5} top={4}>
                {subTitle}
              </Paragraph>
            )}
          </Textfit>
        </TitleWrapper>
      </Box>
      {nrInCart > 0 && (
        <NrInCart showImage={Boolean(storage.showImagesOnButtons && imageUrl)} color={color}>
          <Heading level={5} color={'var(--colorBlueGray1200)'}>
            {nrInCart}
          </Heading>
        </NrInCart>
      )}
      {(inventoryStatus.outOfStock || inventoryStatus.count !== null) && (
        <QuantityWrapper>
          <Heading level={6} color={'var(--colorWhite)'}>
            {inventoryStatus.outOfStock ? i18n('POS.OutOfStock') : inventoryStatus.count ?? ''}
          </Heading>
        </QuantityWrapper>
      )}
      {isPartOfCompetition && <Trophy>✨</Trophy>}
    </ButtonWrapper>
  );
};

export default React.memo(Button);
